import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { mountResourceList } from '../lib/resource-list.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';
import { upgradeColorInputs } from '../lib/color-picker.js';

/**
 * The template editor.
 *
 * Two levels of repeatable rows — embeds, and fields inside each embed — where
 * the other pages have only one. The rule does not change: a row is read
 * **relative to itself**, through `data-field`, and never through an indexed
 * name. An index breaks when a middle row is deleted, and the failure is
 * silent: the page saves the wrong embed.
 */

interface Template {
  _id: string;
  name: string;
  description: string;
  scope: string;
  content: string;
  channelId: string;
  embeds: Record<string, unknown>[];
  rows: { components: Record<string, unknown>[] }[];
  allowedMentions: { users: boolean; roles: boolean; everyone: boolean };
  messageId: string | null;
}

interface ListPageData {
  templates: Template[];
  guildId: string;
}

interface EditorPageData {
  template: Template;
  guildId: string;
}

/**
 * The template list.
 *
 * It no longer carries an editor: editing opens a page. All that remains here
 * is publishing, which is an action rather than a setting.
 */
export function initEmbedsListPage(): void {
  /*
   * The anchor is checked first, like every other page controller: `pageData()`
   * throws when `#page-data` is absent, and every page that is not this one —
   * the home page, the catalogue, the legal pages — was reporting a startup
   * failure to the console of every visitor. Noise that loud hides the failure
   * that matters.
   */
  const list = $('#template-list');
  if (!list) return;

  const { guildId } = pageData<ListPageData>();

  mountResourceList({
    endpoint: 'embeds',
    prefix: 'template',
    guildId,
    listSelector: '#template-list',
    successMessage: ct('templateSaved', 'Template saved.'),
  });
}

/** Editing a template, on a page of its own. */
export function initEmbedsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-template-form]');
  if (!form) return;

  const { template, guildId } = pageData<EditorPageData>();

  wireLists(form);

  // The form is filled once, on load: no dialogue to reopen any more, and so
  // no state to reset between two resources.
  fillForm(form, template as unknown as Record<string, unknown>);
  rebuild(form, 'embed', template.embeds);
  rebuild(form, 'row', template.rows);

  // The scope was read on save but never shown back: a template meant for the
  // Welcome module reopened on "All modules".
  for (const [field, value] of [
    ['channelId', template.channelId],
    ['scope', template.scope],
  ] as const) {
    const component = findSelectMenu(form, field);
    if (component) component.value = value ? [value] : [];
  }

  for (const kind of ['users', 'roles', 'everyone'] as const) {
    const box = form.elements.namedItem(`allowedMentions.${kind}`);
    if (box instanceof HTMLInputElement) box.checked = template.allowedMentions[kind];
  }

  refreshEmptyStates(form);

  mountResourceEditor({
    endpoint: 'embeds',
    listUrl: `/modules/embeds?guildId=${guildId}`,
    guildId,
    form,
    itemId: template._id,

    serialize: () => ({
      name: text(form, 'name'),
      description: text(form, 'description'),
      scope: findSelectMenu(form, 'scope')?.single ?? '',
      channelId: findSelectMenu(form, 'channelId')?.single ?? '',
      content: text(form, 'content'),
      embeds: rows(form, 'embed').map(readEmbed),
      rows: rows(form, 'row')
        .map(readRow)
        .filter((row) => row.components.length > 0),
      allowedMentions: {
        users: checked(form, 'allowedMentions.users'),
        roles: checked(form, 'allowedMentions.roles'),
        everyone: checked(form, 'allowedMentions.everyone'),
      },
      publish: false,
    }),

    labels: {
      saved: ct('templateSaved', 'Template saved.'),
      confirmDelete: ct('confirmDeleteTemplate', 'Delete this template?'),
      deleted: ct('templateDeleted', 'Template deleted.'),
    },
  });
}

/* ------------------------------------------------------------------ lecture */

function readEmbed(row: HTMLElement): Record<string, unknown> {
  return {
    color: value(row, 'color') || '#5865F2',
    authorName: value(row, 'authorName'),
    authorIconUrl: '',
    authorUrl: '',
    title: value(row, 'title'),
    titleUrl: '',
    description: value(row, 'description'),
    fields: $$<HTMLElement>('[data-field-row]', row).map((field) => ({
      name: value(field, 'name'),
      value: value(field, 'value'),
      inline: boxed(field, 'inline'),
    })),
    imageUrl: value(row, 'imageUrl'),
    thumbnailUrl: value(row, 'thumbnailUrl'),
    footerText: value(row, 'footerText'),
    footerIconUrl: '',
    timestamp: boxed(row, 'timestamp'),
  };
}

function readRow(row: HTMLElement): { components: Record<string, unknown>[] } {
  return {
    components: $$<HTMLElement>('[data-component-item]', row)
      .map((item) => ({
        kind: 'button',
        style: 'link',
        label: value(item, 'label'),
        url: value(item, 'url'),
        emoji: '',
        actionId: '',
        placeholder: '',
        minValues: 1,
        maxValues: 1,
        options: [],
      }))
      // A button with no label or no address would be refused by the API: the
      // row somebody started and never filled in is dropped, rather than
      // failing the whole save.
      .filter((component) => component.label !== '' && component.url !== ''),
  };
}

/* -------------------------------------------------------------------- lignes */

let added = 0;

function wireLists(form: HTMLFormElement): void {
  form.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const add = target.closest<HTMLElement>('[data-add]');
    if (add) {
      addRow(
        form,
        add.dataset.add ?? '',
        $(`[data-list="${CSS.escape(add.dataset.add ?? '')}"]`, form),
      );
      return;
    }

    const addField = target.closest<HTMLElement>('[data-add-field]');
    if (addField) {
      const block = addField.closest<HTMLElement>('[data-embed-row]');
      addRow(form, 'field', block ? $('[data-list]', block) : null);
      return;
    }

    const addComponent = target.closest<HTMLElement>('[data-add-component]');
    if (addComponent) {
      const block = addComponent.closest<HTMLElement>('[data-component-row]');
      addRow(form, 'component', block ? $('[data-list]', block) : null);
      return;
    }

    const remove = target.closest('[data-remove]');
    if (remove) {
      remove
        .closest('[data-embed-row], [data-component-row], [data-field-row], [data-component-item]')
        ?.remove();
      refreshEmptyStates(form);
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function addRow(form: HTMLFormElement, kind: string, list: HTMLElement | null): void {
  const blueprint = $<HTMLTemplateElement>(`[data-template="${CSS.escape(kind)}"]`);
  if (!blueprint || !list) return;

  added += 1;

  const holder = document.createElement('div');
  holder.innerHTML = blueprint.innerHTML.replaceAll('__ROW__', `n${String(added)}`);

  const node = holder.firstElementChild;
  if (!node) return;

  list.append(node);

  // The colours and menus of the row template are still native: they lived
  // inside a `<template>`, which nothing walked at startup.
  upgradeSelects(document);
  upgradeColorInputs(document);

  refreshEmptyStates(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Rebuilds a list from the data, going back through the row templates. */
function rebuild(form: HTMLFormElement, kind: string, items: Record<string, unknown>[]): void {
  const list = $<HTMLElement>(`[data-list="${CSS.escape(kind)}"]`, form);
  if (!list) return;

  list.replaceChildren();

  for (const item of items) {
    addRow(form, kind, list);
    const row = list.lastElementChild;
    if (!(row instanceof HTMLElement)) continue;

    if (kind === 'embed') fillEmbed(row, item, form);
    if (kind === 'row') fillRow(row, item, form);
  }
}

function fillEmbed(row: HTMLElement, data: Record<string, unknown>, form: HTMLFormElement): void {
  for (const key of [
    'color',
    'authorName',
    'title',
    'description',
    'imageUrl',
    'thumbnailUrl',
    'footerText',
  ]) {
    setValue(row, key, str(data[key]));
  }
  // The picker was built with the row, before this value existed.
  notify(row, 'color');
  setChecked(row, 'timestamp', data.timestamp === true);

  const list = $('[data-list]', row);
  const fields = Array.isArray(data.fields) ? (data.fields as Record<string, unknown>[]) : [];

  for (const field of fields) {
    addRow(form, 'field', list);
    const node = list?.lastElementChild;
    if (!(node instanceof HTMLElement)) continue;

    setValue(node, 'name', str(field.name));
    setValue(node, 'value', str(field.value));
    setChecked(node, 'inline', field.inline === true);
  }
}

function fillRow(row: HTMLElement, data: Record<string, unknown>, form: HTMLFormElement): void {
  const list = $('[data-list]', row);
  const components = Array.isArray(data.components)
    ? (data.components as Record<string, unknown>[])
    : [];

  for (const component of components) {
    addRow(form, 'component', list);
    const node = list?.lastElementChild;
    if (!(node instanceof HTMLElement)) continue;

    setValue(node, 'label', str(component.label));
    setValue(node, 'url', str(component.url));
  }
}

function refreshEmptyStates(form: HTMLFormElement): void {
  for (const note of $$<HTMLElement>('[data-empty]', form)) {
    const kind = note.dataset.empty ?? '';
    const list = $<HTMLElement>(`[data-list="${CSS.escape(kind)}"]`, note.parentElement ?? form);
    note.hidden = (list?.children.length ?? 0) > 0;
  }
}

function rows(form: HTMLFormElement, kind: string): HTMLElement[] {
  const selector = kind === 'embed' ? '[data-embed-row]' : '[data-component-row]';
  return $$<HTMLElement>(`[data-list="${CSS.escape(kind)}"] > ${selector}`, form);
}

/**
 * A value from the server, reduced to a string.
 *
 * `String(value)` on an object returns "[object Object]" and drops it quietly
 * into a form field. What arrives here is JSON whose shape is described but not
 * guaranteed: a field renamed on the server would slip through in silence.
 */
function str(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/* ------------------------------------------------------------------- champs */

function field(root: ParentNode, name: string): Element | null {
  return root.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

function value(root: ParentNode, name: string): string {
  const node = field(root, name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function boxed(root: ParentNode, name: string): boolean {
  const node = field(root, name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

function setValue(root: ParentNode, name: string, next: string): void {
  const node = field(root, name);
  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) node.value = next;
}

/** Tells a component wrapped around a field that its value was set from code. */
function notify(root: ParentNode, name: string): void {
  field(root, name)?.dispatchEvent(new Event('input', { bubbles: true }));
}

function setChecked(root: ParentNode, name: string, next: boolean): void {
  const node = field(root, name);
  if (node instanceof HTMLInputElement) node.checked = next;
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}
